import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import zoomSdk from '@zoom/appssdk';
import { firstValueFrom, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';
import { MEETING_EMBED_SPACE_QUERY_PARAMS } from '../common/utils/actions-query-params-util';
import { URLService } from './dynamic-url.service';
import { ThirdPartyLoginService } from './third-party-login.service';
import {
  ThirdPartyMeetingSessionResponse,
  ThirdPartySsoBootstrapResult,
  ZoomInClientAuth,
  ZoomSessionRequest,
} from './third-party-sso.types';

/** 48 octets → 64-char base64url verifier (RFC 7636 allows 43–128). */
const PKCE_VERIFIER_BYTE_LENGTH = 48;

@Injectable({
  providedIn: 'root',
})
export class ZoomLoginService {
  constructor(
    private readonly http: HttpClient,
    private readonly urlService: URLService,
    private readonly translate: TranslateService,
    private readonly thirdPartyLoginService: ThirdPartyLoginService,
  ) {}

  /**
   * Full Zoom in-meeting SSO bootstrap for {@link ThirdPartySsoGuard}.
   *
   * Side effects: Zoom Apps SDK config, meeting UUID, user context, optional
   * host topic, in-client OAuth (PKCE), `POST /zoom/session` (BE keys the
   * Space by the meeting instance UUID).
   * Failures propagate to the guard for sign-out, toast, and login redirect.
   */
  async bootstrapZoomSso(): Promise<ThirdPartySsoBootstrapResult> {
    await zoomSdk.config({
      capabilities: [
        'authorize',
        'onAuthorized',
        'getUserContext',
        'getMeetingUUID',
        'getMeetingContext',
      ],
    });

    const meetingUuidResult = await zoomSdk.getMeetingUUID();
    /**
     * Prefer parentUUID in breakouts so SSO keys the same Space as the main
     * meeting. meetingUUID is the current room (breakout or main); parentUUID
     * is only set in a breakout and is the parent instance UUID.
     */
    const meetingUuid =
      meetingUuidResult.parentUUID?.trim() || meetingUuidResult.meetingUUID?.trim() || '';
    if (!meetingUuid) {
      throw new Error(
        this.translate.instant(
          'Pencil is only available inside a Zoom meeting. Open the app from a meeting to continue.',
        ),
      );
    }

    // Meeting role ('host' | 'coHost' | 'attendee' | 'panelist'). The API only
    // lets a host/co-host create the Space; others join it once it exists.
    const { role } = await zoomSdk.getUserContext();
    const sessionAuth = await this.authorizeInClient();

    const { token: firebaseCustomToken, space: spaceId } = await firstValueFrom(
      this.authenticateZoomUser({
        ...sessionAuth,
        meetingUuid,
        meetingTitle: await this.getMeetingTitleForRole(role),
        role,
      }),
    );

    return {
      firebaseCustomToken,
      continueUrl: this.thirdPartyLoginService.buildSpaceContinueUrl(
        spaceId,
        MEETING_EMBED_SPACE_QUERY_PARAMS,
      ),
    };
  }

  /**
   * `getMeetingContext` is host/co-host only. Other roles skip it and send no title.
   * Failures for a host or co-host propagate instead of being treated as "no title".
   */
  private async getMeetingTitleForRole(role: string): Promise<string | undefined> {
    if (role !== 'host' && role !== 'coHost') {
      return undefined;
    }

    const meetingContext = await zoomSdk.getMeetingContext();
    const topic = meetingContext.meetingTopic?.trim();
    return topic || undefined;
  }

  private authenticateZoomUser(
    body: ZoomSessionRequest,
  ): Observable<ThirdPartyMeetingSessionResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      [ErrorInterceptorSkipHeader]: '',
    });
    const url = `${this.urlService.getDynamicUrl()}/zoom/session`;
    return this.http.post<ThirdPartyMeetingSessionResponse>(url, body, { headers });
  }

  /** In-client OAuth: PKCE `plain` (challenge === verifier). Register `onAuthorized` before `authorize`. */
  private authorizeInClient(): Promise<ZoomInClientAuth> {
    const codeVerifier = this.generatePlainPkceVerifier();

    return new Promise((resolve, reject) => {
      zoomSdk.onAuthorized((event) => {
        if (!event.result || !event.code?.trim()) {
          reject(
            new Error(
              this.translate.instant(
                'Zoom authorization was declined. Please approve access to continue.',
              ),
            ),
          );
          return;
        }
        resolve({
          authorizationCode: event.code,
          codeVerifier,
          redirectUri: event.redirectUri,
        });
      });

      zoomSdk
        .authorize({ codeChallenge: codeVerifier })
        .catch((error) => reject(error instanceof Error ? error : new Error(String(error))));
    });
  }

  /** RFC 7636 verifier; Zoom in-client OAuth only supports PKCE method `plain`. */
  private generatePlainPkceVerifier(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(PKCE_VERIFIER_BYTE_LENGTH));
    const base64 = btoa(String.fromCharCode(...bytes));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
